import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'

import { api, type BoardingCreateBody, type GroomingCreateBody } from './api/client'
import {
  ApiError,
  type BookingResult,
  type BusinessProfile,
  type Groomer,
  type GroomingCatalog,
  type GroomingSlot,
  type PricingPreview,
  type RoomCard,
} from './api/types'
import { Banner, BizGallery, BizHeader, BizVideo, LangSwitch, Spinner, Stepper } from './ui'
import {
  AddOnPicker,
  type AddOnSelection,
  addOnsSubtotalMinor,
  BuyPackageHeaderLink,
  nightsNeeded,
  PackageCheckoutReturn,
  RedeemPackPicker,
} from './Packages'

type Step = 'service' | 'dates' | 'addon' | 'details' | 'verify' | 'review' | 'done'
const VALID_SOURCES = ['own_site', 'line', 'google', 'cherie']

export interface LiffContext {
  idToken: string
  name?: string
}

const todayISO = () => new Date().toISOString().slice(0, 10)
const addDaysISO = (iso: string, days: number) =>
  new Date(Date.parse(iso) + days * 86400000).toISOString().slice(0, 10)
const money = (v: string | number | null | undefined) =>
  v == null ? '' : `NT$${Number(v).toLocaleString()}`
// Grooming slot times come from the backend as clinic-local times labelled UTC,
// so format them in UTC to show the intended local time (not the viewer's TZ).
const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })

type GroomPick = {
  kind: 'service' | 'package'
  id: string
  name: string
  price: string | number
  duration: number
}

export default function BookingPage({
  slugOverride,
  liff,
}: {
  slugOverride?: string
  liff?: LiffContext
} = {}) {
  const { slug: slugParam = '' } = useParams<{ slug: string }>()
  const slug = slugOverride ?? slugParam
  const [sp] = useSearchParams()
  const { t } = useTranslation()

  const isLiff = !!liff
  const source = useMemo(() => {
    if (isLiff) return 'line'
    const s = sp.get('src') || 'own_site'
    return VALID_SOURCES.includes(s) ? s : 'own_site'
  }, [sp, isLiff])

  // LIFF skips phone-OTP — the LINE id_token mints the booking token at submit.
  const baseSteps: Step[] = isLiff
    ? ['service', 'dates', 'details', 'review', 'done']
    : ['service', 'dates', 'details', 'verify', 'review', 'done']

  const [profile, setProfile] = useState<BusinessProfile | null>(null)
  const [notFound, setNotFound] = useState(false)

  const [step, setStep] = useState<Step>('service')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // selections
  const [service, setService] = useState<'boarding' | 'daycare' | 'grooming'>('boarding')
  const [checkIn, setCheckIn] = useState('')
  const [checkOut, setCheckOut] = useState('')
  const [rooms, setRooms] = useState<RoomCard[] | null>(null)
  const [room, setRoom] = useState<RoomCard | null>(null)
  const [pricing, setPricing] = useState<PricingPreview | null>(null)

  // grooming selections
  const [groomCatalog, setGroomCatalog] = useState<GroomingCatalog | null>(null)
  const [groomPick, setGroomPick] = useState<GroomPick | null>(null)
  const [groomDate, setGroomDate] = useState('')
  const [slots, setSlots] = useState<GroomingSlot[] | null>(null)
  const [slot, setSlot] = useState<GroomingSlot | null>(null)
  const [groomers, setGroomers] = useState<Groomer[] | null>(null)
  const [groomerId, setGroomerId] = useState<string | null>(null) // null = any available
  const [addGrooming, setAddGrooming] = useState(false)
  // C3 — selected PackagePurchase id for redeem-at-booking
  const [pickedPackId, setPickedPackId] = useState<string | null>(null)
  // ADDONS-4 — selected add-ons (per-leg via serviceType on each entry)
  const [addOns, setAddOns] = useState<AddOnSelection[]>([])

  // Boarding/daycare bookings can optionally add a grooming session to the same
  // order — an extra "addon" step after dates (only when the business grooms).
  const wantsAddon = service !== 'grooming' && !!profile?.accepts_grooming
  const steps: Step[] = []
  for (const s of baseSteps) {
    steps.push(s)
    if (s === 'dates' && wantsAddon) steps.push('addon')
  }

  const [petName, setPetName] = useState('')
  const [species, setSpecies] = useState('dog')
  const [breed, setBreed] = useState('')
  const [ownerName, setOwnerName] = useState(liff?.name ?? '')
  const [phone, setPhone] = useState('')
  const [instructions, setInstructions] = useState('')

  // identity
  const [ticket, setTicket] = useState('')
  const [devCode, setDevCode] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [bookingToken, setBookingToken] = useState('')

  const [result, setResult] = useState<BookingResult | null>(null)

  // load profile + apply per-business accent colour
  useEffect(() => {
    let alive = true
    api
      .getProfile(slug)
      .then((p) => {
        if (!alive) return
        setProfile(p)
        if (p.accent_color) {
          document.documentElement.style.setProperty('--accent', p.accent_color)
          document.documentElement.style.setProperty('--accent-dark', p.accent_color)
        }
      })
      .catch(() => alive && setNotFound(true))
    return () => {
      alive = false
    }
  }, [slug])

  function errMsg(e: unknown): string {
    if (e instanceof ApiError) {
      if (e.status === 429) return t('errors.rateLimited')
      if (e.message.startsWith('errors.')) return t(e.message)
      return e.message || t('errors.generic')
    }
    if (e instanceof Error && e.message.startsWith('errors.')) return t(e.message)
    return t('errors.generic')
  }

  const go = (s: Step) => {
    setError(null)
    setStep(s)
  }

  // ---- step actions -------------------------------------------------------
  async function searchRooms() {
    setBusy(true)
    setError(null)
    try {
      const ci = checkIn
      const co = service === 'daycare' ? checkIn : checkOut
      const list = await api.getAvailability(slug, {
        check_in: ci,
        check_out: co,
        service_type: service,
      })
      setRooms(list)
      setRoom(null)
      setPricing(null)
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusy(false)
    }
  }

  async function selectRoom(r: RoomCard) {
    setRoom(r)
    setPricing(null)
    try {
      const co = service === 'daycare' ? checkIn : checkOut
      const p = await api.pricingPreview(slug, {
        room_id: r.id,
        check_in_date: checkIn,
        check_out_date: co,
        service_type: service,
      })
      setPricing(p)
    } catch {
      /* pricing is best-effort; the booking still works without it */
    }
  }

  async function loadGroomCatalog() {
    if (groomCatalog) return
    try {
      const [cat, grms] = await Promise.all([
        api.getGroomingServices(slug),
        api.getGroomers(slug).catch(() => [] as Groomer[]),
      ])
      setGroomCatalog(cat)
      setGroomers(grms)
    } catch (e) {
      setError(errMsg(e))
    }
  }

  async function pickGrooming() {
    setService('grooming')
    await loadGroomCatalog()
  }

  async function searchSlots() {
    if (!groomPick || !groomDate) return
    setBusy(true)
    setError(null)
    try {
      const list = await api.getGroomingSlots(slug, {
        date: groomDate,
        service_id: groomPick.kind === 'service' ? groomPick.id : undefined,
        package_id: groomPick.kind === 'package' ? groomPick.id : undefined,
        groomer_id: groomerId ?? undefined,
      })
      setSlots(list)
      setSlot(null)
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusy(false)
    }
  }

  async function sendCode() {
    setBusy(true)
    setError(null)
    try {
      const resp = await api.identityStart(slug, { phone, name: ownerName, source })
      setTicket(resp.identity_ticket)
      setDevCode(resp.dev_code)
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusy(false)
    }
  }

  async function verifyCode() {
    setBusy(true)
    setError(null)
    try {
      const resp = await api.identityVerify(slug, { identity_ticket: ticket, code })
      setBookingToken(resp.booking_token)
      go('review')
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusy(false)
    }
  }

  async function submitBooking() {
    setBusy(true)
    setError(null)
    try {
      let token = bookingToken
      if (isLiff && !token && liff) {
        // Mint the booking token from the verified LINE id_token + the phone the
        // visitor entered (OTP was skipped).
        const r = await api.identityLiff(slug, {
          line_id_token: liff.idToken,
          phone,
          name: ownerName,
        })
        token = r.booking_token
        setBookingToken(token)
      }
      const hasAddon = service !== 'grooming' && addGrooming && !!groomPick && !!slot
      // ADDONS-4 — map selections → API shape, partitioned by leg.
      const addOnsFor = (st: 'boarding' | 'daycare' | 'grooming') =>
        addOns
          .filter((a) => a.serviceType === st)
          .map((a) => ({
            id: a.id,
            quantity: a.quantity,
            price_minor: a.price_minor,
          }))
      let res: BookingResult
      if (service === 'grooming') {
        if (!groomPick || !slot) throw new Error('errors.generic')
        const gbody: GroomingCreateBody = {
          booking_token: token,
          pet_name: petName,
          species,
          breed: breed || undefined,
          service_id: groomPick.kind === 'service' ? groomPick.id : undefined,
          package_id: groomPick.kind === 'package' ? groomPick.id : undefined,
          groomer_id: slot.groomer_id,
          scheduled_date: groomDate,
          scheduled_start: slot.start_time,
          scheduled_end: slot.end_time,
          special_requests: instructions || undefined,
          package_purchase_id: pickedPackId || undefined,
          add_ons: addOnsFor('grooming'),
        }
        res = await api.createGrooming(slug, gbody)
      } else if (hasAddon && groomPick && slot) {
        // Boarding/daycare + a grooming add-on → one atomic order.
        const co = service === 'daycare' ? checkIn : checkOut
        const cres = await api.createCombined(slug, {
          booking_token: token,
          pet_name: petName,
          species,
          breed: breed || undefined,
          boarding: {
            service_type: service,
            check_in_date: checkIn,
            check_out_date: co,
            room_id: room?.id,
            special_instructions: instructions || undefined,
            // C3 — package only applies to boarding leg in combined flow;
            // grooming leg uses its own redemption picker (omitted for v1).
            package_purchase_id: pickedPackId || undefined,
            add_ons: addOnsFor(service),
          },
          grooming: {
            service_id: groomPick.kind === 'service' ? groomPick.id : undefined,
            package_id: groomPick.kind === 'package' ? groomPick.id : undefined,
            groomer_id: slot.groomer_id,
            scheduled_date: groomDate,
            scheduled_start: slot.start_time,
            scheduled_end: slot.end_time,
            add_ons: addOnsFor('grooming'),
          },
        })
        res = {
          id: cres.order_id,
          kind: 'boarding',
          status: '',
          booking_source: '',
          guest_id: null,
          requires_confirmation: cres.requires_confirmation,
          created_at: null,
        }
      } else {
        const co = service === 'daycare' ? checkIn : checkOut
        const body: BoardingCreateBody = {
          booking_token: token,
          pet_name: petName,
          species,
          breed: breed || undefined,
          service_type: service,
          check_in_date: checkIn,
          check_out_date: co,
          special_instructions: instructions || undefined,
          package_purchase_id: pickedPackId || undefined,
          add_ons: addOnsFor(service),
        }
        if (room) body.room_id = room.id
        res = await api.createReservation(slug, body)
      }
      setResult(res)
      go('done')
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        // booking_token expired mid-flow — clear it (LIFF re-mints at submit;
        // phone-OTP goes back to re-verify).
        setBookingToken('')
        setCode('')
        setError(t(isLiff ? 'errors.tokenExpiredLiff' : 'errors.tokenExpired'))
        setStep(isLiff ? 'details' : 'verify')
      } else {
        setError(errMsg(e))
      }
    } finally {
      setBusy(false)
    }
  }

  // ---- render -------------------------------------------------------------
  if (notFound) {
    return (
      <div className="page">
        <LangSwitch />
        <div className="card center" style={{ marginTop: 64 }}>
          <h2>{t('notFound.title')}</h2>
          <p className="muted">{t('notFound.body')}</p>
        </div>
      </div>
    )
  }
  if (!profile) {
    return (
      <div className="page">
        <Spinner />
      </div>
    )
  }

  const stepIndex = steps.indexOf(step)
  const datesReady =
    service === 'grooming'
      ? !!groomPick && !!slot
      : service === 'daycare'
        ? !!checkIn
        : !!checkIn && !!checkOut && checkOut > checkIn
  const detailsReady = !!petName.trim() && !!ownerName.trim() && phone.trim().length >= 6
  const maxDate = addDaysISO(todayISO(), profile.max_future_days)

  // Groomers who can do the chosen service (empty services_offered = all).
  const eligibleGroomers = (groomers ?? []).filter(
    (g) =>
      g.services_offered.length === 0 ||
      (groomPick?.kind === 'service' && g.services_offered.includes(groomPick.id)) ||
      groomPick?.kind === 'package',
  )

  const renderGroomingPicker = () => (
    <>
      <label className="field">{t('grooming.chooseService')}</label>
      {(groomCatalog?.services ?? []).map((s) => (
        <div
          key={s.id}
          className={`card selectable${groomPick?.id === s.id ? ' selected' : ''}`}
          onClick={() => {
            setGroomPick({
              kind: 'service',
              id: s.id,
              name: s.name_zh || s.name,
              price: s.base_price,
              duration: s.base_duration_minutes,
            })
            setSlots(null)
            setSlot(null)
          }}
        >
          <div className="row">
            <strong>{s.name_zh || s.name}</strong>
            <span>{money(s.base_price)}</span>
          </div>
          <div className="muted">{t('grooming.duration', { count: s.base_duration_minutes })}</div>
        </div>
      ))}
      {(groomCatalog?.packages ?? []).map((p) => (
        <div
          key={p.id}
          className={`card selectable${groomPick?.id === p.id ? ' selected' : ''}`}
          onClick={() => {
            setGroomPick({
              kind: 'package',
              id: p.id,
              name: p.name_zh || p.name,
              price: p.package_price,
              duration: p.base_duration_minutes,
            })
            setSlots(null)
            setSlot(null)
          }}
        >
          <div className="row">
            <strong>{p.name_zh || p.name}</strong>
            <span>{money(p.package_price)}</span>
          </div>
          <div className="muted">{t('grooming.duration', { count: p.base_duration_minutes })}</div>
        </div>
      ))}
      {groomPick && eligibleGroomers.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <label className="field">{t('grooming.chooseGroomer')}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button
              type="button"
              className={`card selectable${groomerId === null ? ' selected' : ''}`}
              style={{ flex: '1 1 140px', textAlign: 'left', cursor: 'pointer' }}
              onClick={() => {
                setGroomerId(null)
                setSlots(null)
                setSlot(null)
              }}
            >
              <strong>{t('grooming.anyGroomer')}</strong>
              <div className="muted">{t('grooming.anyGroomerHint')}</div>
            </button>
            {eligibleGroomers.map((g) => (
              <button
                type="button"
                key={g.id}
                className={`card selectable${groomerId === g.id ? ' selected' : ''}`}
                style={{ flex: '1 1 140px', textAlign: 'left', cursor: 'pointer' }}
                onClick={() => {
                  setGroomerId(g.id)
                  setSlots(null)
                  setSlot(null)
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {g.photo_url ? (
                    <img
                      src={g.photo_url}
                      alt={g.name_zh || g.name}
                      style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: 'var(--border-light)',
                        display: 'grid',
                        placeItems: 'center',
                        fontWeight: 600,
                      }}
                    >
                      {(g.name_zh || g.name || '?').slice(0, 1)}
                    </div>
                  )}
                  <strong>{g.name_zh || g.name}</strong>
                </div>
                {g.specialties.length > 0 && (
                  <div className="muted" style={{ marginTop: 4 }}>
                    {g.specialties.join(' · ')}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
      <div>
        <label className="field">{t('grooming.date')}</label>
        <input
          type="date"
          min={todayISO()}
          max={maxDate}
          value={groomDate}
          onChange={(e) => {
            setGroomDate(e.target.value)
            setSlots(null)
            setSlot(null)
          }}
        />
      </div>
      <button
        className="btn btn-primary"
        disabled={!groomPick || !groomDate || busy}
        onClick={searchSlots}
      >
        {busy ? t('loading') : t('grooming.findTimes')}
      </button>
      {slots && slots.length === 0 && <Banner kind="info">{t('grooming.noSlots')}</Banner>}
      {slots && slots.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {slots.map((s) => (
            <button
              key={`${s.groomer_id}-${s.start_time}`}
              className={`btn ${
                slot?.start_time === s.start_time && slot?.groomer_id === s.groomer_id
                  ? 'btn-primary'
                  : 'btn-ghost'
              }`}
              style={{ flex: '0 0 auto', minWidth: 72 }}
              onClick={() => setSlot(s)}
            >
              {fmtTime(s.start_time)}
              {groomerId === null && s.groomer_name && (
                <span style={{ display: 'block', fontSize: 11, opacity: 0.75 }}>
                  {s.groomer_name}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </>
  )

  return (
    <div className="page">
      <PackageCheckoutReturn slug={slug!} />
      <LangSwitch />
      <BizHeader profile={profile} />
      <div className="chip-row">
        <BuyPackageHeaderLink slug={slug!} bookingToken={bookingToken || null} />
      </div>
      <BizGallery photos={profile.cover_photos} />
      <BizVideo
        url={profile.intro_video_url}
        provider={profile.intro_video_provider}
      />
      <Stepper current={stepIndex} total={steps.length} />
      {error && <Banner kind="error">{error}</Banner>}

      {step === 'service' && (
        <section className="stack">
          <h2>{t('service.title')}</h2>
          <div
            className={`card selectable${service === 'boarding' ? ' selected' : ''}`}
            onClick={() => setService('boarding')}
          >
            <strong>🏠 {t('service.boarding')}</strong>
            <div className="muted">{t('service.boardingDesc')}</div>
          </div>
          {profile.accepts_daycare && (
            <div
              className={`card selectable${service === 'daycare' ? ' selected' : ''}`}
              onClick={() => setService('daycare')}
            >
              <strong>☀️ {t('service.daycare')}</strong>
              <div className="muted">{t('service.daycareDesc')}</div>
            </div>
          )}
          {profile.accepts_grooming && (
            <div
              className={`card selectable${service === 'grooming' ? ' selected' : ''}`}
              onClick={() => void pickGrooming()}
            >
              <strong>✂️ {t('service.grooming')}</strong>
              <div className="muted">{t('service.groomingDesc')}</div>
            </div>
          )}
          <button className="btn btn-primary" onClick={() => go('dates')}>
            {t('next')}
          </button>
        </section>
      )}

      {step === 'dates' && (
        <section className="stack">
          <h2>{service === 'grooming' ? t('grooming.title') : t('dates.title')}</h2>

          {service === 'grooming' && renderGroomingPicker()}

          {service !== 'grooming' && (
          <>
          <div>
            <label className="field">{t('dates.checkIn')}</label>
            <input
              type="date"
              min={todayISO()}
              max={maxDate}
              value={checkIn}
              onChange={(e) => {
                setCheckIn(e.target.value)
                setRooms(null)
                setRoom(null)
              }}
            />
          </div>
          {service === 'boarding' && (
            <div>
              <label className="field">{t('dates.checkOut')}</label>
              <input
                type="date"
                min={checkIn ? addDaysISO(checkIn, 1) : todayISO()}
                max={maxDate}
                value={checkOut}
                onChange={(e) => {
                  setCheckOut(e.target.value)
                  setRooms(null)
                  setRoom(null)
                }}
              />
            </div>
          )}
          <button className="btn btn-primary" disabled={!datesReady || busy} onClick={searchRooms}>
            {busy ? t('loading') : t('dates.search')}
          </button>

          {rooms && rooms.length === 0 && <Banner kind="info">{t('dates.noRooms')}</Banner>}
          {rooms && rooms.length > 0 && (
            <div className="stack">
              {rooms.map((r) => (
                <div
                  key={r.id}
                  className={`card selectable${room?.id === r.id ? ' selected' : ''}`}
                  onClick={() => void selectRoom(r)}
                >
                  <div className="row">
                    <strong>{r.name}</strong>
                    {r.daily_rate != null && (
                      <span>
                        {money(r.daily_rate)}
                        <span className="muted">{t('dates.perNight')}</span>
                      </span>
                    )}
                  </div>
                  <div className="muted">{t('dates.capacity', { count: r.capacity })}</div>
                </div>
              ))}
            </div>
          )}

          {room && pricing?.total_amount != null && (
            <Banner kind="info">
              {t('review.dates')}: {money(pricing.total_amount)}
              {pricing.nights ? ` · ${pricing.nights} 晚` : ''}
            </Banner>
          )}
          </>
          )}

          <div className="row">
            <button className="btn btn-ghost" onClick={() => go('service')}>
              {t('back')}
            </button>
            <button
              className="btn btn-primary"
              disabled={service === 'grooming' ? !slot : !room}
              onClick={() => go(wantsAddon ? 'addon' : 'details')}
            >
              {t('next')}
            </button>
          </div>
        </section>
      )}

      {step === 'addon' && (
        <section className="stack">
          <h2>{t('addon.title')}</h2>
          <p className="muted">{t('addon.subtitle')}</p>
          <div className="row">
            <button
              className={`btn ${addGrooming ? 'btn-ghost' : 'btn-primary'}`}
              onClick={() => {
                setAddGrooming(false)
                setGroomPick(null)
                setSlots(null)
                setSlot(null)
              }}
            >
              {t('addon.skip')}
            </button>
            <button
              className={`btn ${addGrooming ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => {
                setAddGrooming(true)
                if (!groomDate) setGroomDate(checkIn)
                void loadGroomCatalog()
              }}
            >
              {t('addon.add')}
            </button>
          </div>
          {addGrooming && renderGroomingPicker()}
          <div className="row">
            <button className="btn btn-ghost" onClick={() => go('dates')}>
              {t('back')}
            </button>
            <button
              className="btn btn-primary"
              disabled={addGrooming && (!groomPick || !slot)}
              onClick={() => go('details')}
            >
              {t('next')}
            </button>
          </div>
        </section>
      )}

      {step === 'details' && (
        <section className="stack">
          <h2>{t('details.title')}</h2>
          <div>
            <label className="field">{t('details.petName')}</label>
            <input value={petName} onChange={(e) => setPetName(e.target.value)} />
          </div>
          <div>
            <label className="field">{t('details.species')}</label>
            <select value={species} onChange={(e) => setSpecies(e.target.value)}>
              <option value="dog">🐶 {t('details.dog')}</option>
              <option value="cat">🐱 {t('details.cat')}</option>
              <option value="other">🐾 {t('details.other')}</option>
            </select>
          </div>
          <div>
            <label className="field">{t('details.breed')}</label>
            <input value={breed} onChange={(e) => setBreed(e.target.value)} />
          </div>
          <div>
            <label className="field">{t('details.ownerName')}</label>
            <input value={ownerName} onChange={(e) => setOwnerName(e.target.value)} />
          </div>
          <div>
            <label className="field">{t('details.phone')}</label>
            <input
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <div className="muted">{t('details.phoneHint')}</div>
          </div>
          <div>
            <label className="field">{t('details.specialInstructions')}</label>
            <textarea rows={2} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
          </div>
          <div className="row">
            <button className="btn btn-ghost" onClick={() => go(wantsAddon ? 'addon' : 'dates')}>
              {t('back')}
            </button>
            <button
              className="btn btn-primary"
              disabled={!detailsReady}
              onClick={() => {
                if (isLiff) {
                  go('review')
                } else {
                  go('verify')
                  setDevCode(null)
                  setTicket('')
                }
              }}
            >
              {t('next')}
            </button>
          </div>
        </section>
      )}

      {step === 'verify' && (
        <section className="stack">
          <h2>{t('verify.title')}</h2>
          {!ticket ? (
            <>
              <p className="muted">{t('details.phoneHint')}</p>
              <button className="btn btn-primary" disabled={busy} onClick={sendCode}>
                {busy ? t('loading') : t('verify.sendCode')}
              </button>
            </>
          ) : (
            <>
              <Banner kind="info">{t('verify.sent', { phone })}</Banner>
              {devCode && <Banner kind="dev">{t('verify.devCode', { code: devCode })}</Banner>}
              <div>
                <label className="field">{t('verify.codeLabel')}</label>
                <input
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                />
              </div>
              <button
                className="btn btn-primary"
                disabled={busy || code.length < 4}
                onClick={verifyCode}
              >
                {busy ? t('verify.verifying') : t('next')}
              </button>
              <button className="btn btn-ghost" disabled={busy} onClick={sendCode}>
                {t('verify.resend')}
              </button>
            </>
          )}
          <button className="btn btn-ghost" onClick={() => go('details')}>
            {t('back')}
          </button>
        </section>
      )}

      {step === 'review' && (
        <section className="stack">
          <h2>{t('review.title')}</h2>
          {/* C3 — redeem-pack picker (only renders if customer has an active pack
              matching this service_type with enough remaining sessions). */}
          <RedeemPackPicker
            slug={slug!}
            bookingToken={bookingToken || null}
            serviceType={service}
            nightsNeeded={nightsNeeded(service, checkIn, checkOut)}
            selected={pickedPackId}
            onChange={setPickedPackId}
          />
          {/* ADDONS-4 — per-item upsells. For a combined boarding+grooming
              order we show grouped per-leg sections; otherwise a flat list. */}
          <AddOnPicker
            slug={slug!}
            legs={
              service !== 'grooming' && addGrooming && groomPick && slot
                ? [
                    { serviceType: service, label: t(`service.${service}`) },
                    { serviceType: 'grooming', label: t('service.grooming') },
                  ]
                : [{ serviceType: service }]
            }
            selections={addOns}
            onChange={setAddOns}
          />
          <div className="card stack">
            <div className="row">
              <span className="muted">{t('review.service')}</span>
              <span>{t(`service.${service}`)}</span>
            </div>
            {service === 'grooming' ? (
              <>
                <div className="row">
                  <span className="muted">{t('grooming.chooseService')}</span>
                  <span>{groomPick?.name}</span>
                </div>
                <div className="row">
                  <span className="muted">{t('review.dates')}</span>
                  <span>{groomDate}</span>
                </div>
                {slot && (
                  <div className="row">
                    <span className="muted">{t('grooming.time')}</span>
                    <span>
                      {fmtTime(slot.start_time)}–{fmtTime(slot.end_time)}
                    </span>
                  </div>
                )}
                {slot?.groomer_name && (
                  <div className="row">
                    <span className="muted">{t('grooming.chooseGroomer')}</span>
                    <span>{slot.groomer_name}</span>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="row">
                  <span className="muted">{t('review.dates')}</span>
                  <span>
                    {checkIn}
                    {service === 'boarding' ? ` → ${checkOut}` : ''}
                  </span>
                </div>
                {room && (
                  <div className="row">
                    <span className="muted">{t('dates.room')}</span>
                    <span>{room.name}</span>
                  </div>
                )}
              </>
            )}
            {service !== 'grooming' && addGrooming && groomPick && slot && (
              <>
                <div className="row">
                  <span className="muted">{t('addon.tag')}</span>
                  <span>{groomPick.name}</span>
                </div>
                <div className="row">
                  <span className="muted">{t('grooming.time')}</span>
                  <span>
                    {groomDate} · {fmtTime(slot.start_time)}
                  </span>
                </div>
              </>
            )}
            <div className="row">
              <span className="muted">{t('review.pet')}</span>
              <span>{petName}</span>
            </div>
            <div className="row">
              <span className="muted">{t('review.contact')}</span>
              <span>
                {ownerName} · {phone}
              </span>
            </div>
            {service === 'grooming' && groomPick && (
              <div className="row">
                <strong>{t('review.service')}</strong>
                <strong>{money(groomPick.price)}</strong>
              </div>
            )}
            {service !== 'grooming' && pricing?.total_amount != null && (
              <div className="row">
                <strong>{t('review.dates')}</strong>
                <strong>{money(pricing.total_amount)}</strong>
              </div>
            )}
            {service !== 'grooming' && addGrooming && groomPick && (
              <div className="row">
                <strong>{t('addon.tag')}</strong>
                <strong>{money(groomPick.price)}</strong>
              </div>
            )}
            {/* ADDONS-4 — add-on line items + subtotal */}
            {addOns.map((a) => (
              <div className="row" key={`${a.serviceType}:${a.id}`}>
                <span className="muted">
                  {a.name_zh}
                  {a.quantity > 1 ? ` ×${a.quantity}` : ''}
                </span>
                <span>{money((a.price_minor * a.quantity) / 100)}</span>
              </div>
            ))}
            {addOns.length > 0 && (
              <div className="row">
                <strong>{t('addon.total')}</strong>
                <strong>{money(addOnsSubtotalMinor(addOns) / 100)}</strong>
              </div>
            )}
          </div>
          <button className="btn btn-primary" disabled={busy} onClick={submitBooking}>
            {busy ? t('review.submitting') : t('confirm')}
          </button>
          <button className="btn btn-ghost" onClick={() => go(isLiff ? 'details' : 'verify')}>
            {t('back')}
          </button>
        </section>
      )}

      {step === 'done' && result && (
        <section className="stack center" style={{ marginTop: 24 }}>
          <div className="result-icon">{result.requires_confirmation ? '📨' : '✅'}</div>
          <h2>{result.requires_confirmation ? t('done.requestedTitle') : t('done.confirmedTitle')}</h2>
          <p className="muted">
            {result.requires_confirmation ? t('done.requestedBody') : t('done.confirmedBody')}
          </p>
          <p className="muted">
            {t('done.reference')}: <code>{result.id}</code>
          </p>
          <button
            className="btn btn-ghost"
            onClick={() => {
              setResult(null)
              setBookingToken('')
              setTicket('')
              setCode('')
              setRooms(null)
              setRoom(null)
              setGroomPick(null)
              setSlots(null)
              setSlot(null)
              setGroomDate('')
              setAddGrooming(false)
              go('service')
            }}
          >
            {t('done.bookAnother')}
          </button>
        </section>
      )}
    </div>
  )
}
